import { useEffect, useRef, useState } from "react";
import { Play, Radio, Volume2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { useCommentary } from "../context/CommentaryContext";

export const WelcomeVideo = ({ src, poster }) => {
  const { playing, play, stop, audioRef } = useCommentary();
  const videoRef = useRef(null);
  const playingRef = useRef(playing);
  const inViewRef = useRef(false);
  const [soundBlocked, setSoundBlocked] = useState(false);

  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  // Silently snap the muted video to the commentary audio (source of truth).
  const alignToCommentary = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a || a.paused) return;
    const t = a.currentTime;
    if (!isFinite(t)) return;
    if (Math.abs(v.currentTime - t) > 0.35) {
      try {
        v.currentTime = t;
      } catch (e) {
        /* seek not ready yet */
      }
    }
  };

  // Autoplay (with sound) when scrolled into view; pause when it leaves.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        inViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          const commentaryOn = playingRef.current;
          v.muted = commentaryOn; // keep muted only if Pete's commentary is running
          v.play()
            .then(() => {
              if (!commentaryOn) setSoundBlocked(false);
              else alignToCommentary(); // re-enter view mid-commentary -> catch up instantly
            })
            .catch(() => {
              // Browser blocked audible autoplay – fall back to muted playback.
              v.muted = true;
              setSoundBlocked(!commentaryOn);
              v.play()
                .then(() => {
                  if (commentaryOn) alignToCommentary();
                })
                .catch(() => {});
            });
        } else {
          v.pause();
        }
      },
      { threshold: 0.5 }
    );
    io.observe(v);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync-guard watchdog: while commentary runs, keep the visible video aligned to it.
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      if (inViewRef.current && videoRef.current && !videoRef.current.paused) {
        alignToCommentary();
      }
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  // When commentary starts, keep the video playing but muted; when it stops, restore its sound.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.muted = true;
      setSoundBlocked(false);
      if (inViewRef.current && v.paused) v.play().catch(() => {});
    } else {
      v.muted = false;
    }
  }, [playing]);

  const enableSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setSoundBlocked(false);
    if (v.paused) v.play().catch(() => {});
  };

  const onCommentary = () => {
    const v = videoRef.current;
    if (playing) {
      stop(); // commentary off – the effect above unmutes the video
    } else {
      const t = v ? v.currentTime : 0;
      if (v) {
        v.muted = true;
        setSoundBlocked(false);
        if (v.paused) v.play().catch(() => {});
      }
      play(t); // start Pete's commentary in sync with the video's current position
    }
  };

  return (
    <section className="py-20 md:py-28 bg-cloud" data-testid="welcome-video-section">
      <div className="container-px max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="overline">Welcome</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink mt-4 leading-tight">
            A welcome from Pete Ashby
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8">
            {src ? (
              <div className="relative w-full max-w-3xl mx-auto">
                <video
                  ref={videoRef}
                  data-testid="welcome-video"
                  playsInline
                  controls
                  preload="none"
                  poster={poster}
                  className="w-full aspect-video bg-black border border-ink/10"
                >
                  <source src={src} type="video/mp4" />
                </video>
                {soundBlocked && !playing && (
                  <button
                    type="button"
                    onClick={enableSound}
                    data-testid="welcome-video-unmute"
                    className="absolute top-3 right-3 inline-flex items-center gap-2 bg-windsor text-ivory px-4 py-2 text-sm font-medium rounded-full shadow-lg animate-pulse"
                  >
                    <Volume2 size={16} /> Tap for sound
                  </button>
                )}
              </div>
            ) : (
              <div
                data-testid="welcome-video-placeholder"
                className="w-full aspect-video max-w-3xl mx-auto border border-ink/10 bg-ivory grid place-items-center"
              >
                <div className="text-center px-6">
                  <span className="w-16 h-16 grid place-items-center rounded-full bg-windsor text-ivory mx-auto">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                  <p className="text-ink/80 font-light mt-5">
                    Pete's welcome video is arriving shortly.
                  </p>
                </div>
              </div>
            )}
            {src && (
              <button
                data-testid="background-commentary-toggle"
                onClick={onCommentary}
                className={`inline-flex items-center gap-2.5 mt-6 px-7 py-3.5 font-medium tracking-wide transition-colors ${
                  playing ? "bg-ink text-ivory" : "bg-[#C2410C] text-ivory hover:bg-windsor-dark"
                }`}
              >
                <Radio size={17} className={playing ? "animate-pulse" : ""} />
                {playing ? "Commentary playing – tap to stop" : "Play as commentary"}
              </button>
            )}
            {src && (
              <p className="text-ink/80 text-sm mt-3">
                Keeps playing as you explore the site.
              </p>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
